<template>
  <div class="admin-container">
    <!-- PC侧边导航 -->
    <aside class="admin-sidebar">
      <div class="sidebar-header">
        <div class="logo">
          <span class="logo-icon">🛒</span>
          <span class="logo-text">管理后台</span>
        </div>
      </div>
      <nav class="sidebar-nav">
        <div
          class="nav-item"
          v-for="tab in tabs"
          :key="tab.key"
          :class="{ active: activeTab === tab.key }"
          @click="switchTab(tab.key)"
        >
          <span class="nav-icon">{{ tab.icon }}</span>
          <span class="nav-label">{{ tab.label }}</span>
        </div>
      </nav>
      <div class="sidebar-footer">
        <button class="back-btn" @click="$router.push('/')">🏠 返回商城</button>
      </div>
    </aside>

    <!-- 主内容区 -->
    <main class="admin-main">
      <header class="admin-header">
        <div class="header-left">
          <h1 class="page-title">{{ currentTabLabel }}</h1>
        </div>
        <div class="header-right">
          <div class="admin-avatar">
            <span v-if="isSuper">👑 {{ adminUsername || '超级管理员' }}</span>
            <span v-else>🏪 {{ shopName || adminUsername || '商家' }}</span>
          </div>
          <div class="role-tag" :class="isSuper ? 'super' : 'merchant'">
            {{ isSuper ? '超级管理员' : '普通商家' }}
          </div>
          <button class="header-btn service" @click="openAgentWorkbench">💬 客服工作台</button>
          <button class="header-btn logout" @click="adminLogout">🚪 退出</button>
        </div>
      </header>

      <div class="admin-content">
        <!-- ============ 商品管理 ============ -->
        <div v-show="activeTab === 'goods'" class="tab-panel">
          <!-- 统计卡片 -->
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-icon goods-icon">📦</div>
              <div class="stat-info">
                <div class="stat-value">{{ goodsList.length }}</div>
                <div class="stat-label">商品总数</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon on-sale-icon">🟢</div>
              <div class="stat-info">
                <div class="stat-value">{{ goodsList.filter(g => g.is_on_sale === 1).length }}</div>
                <div class="stat-label">已上架</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon off-sale-icon">🔴</div>
              <div class="stat-info">
                <div class="stat-value">{{ goodsList.filter(g => g.is_on_sale === 0).length }}</div>
                <div class="stat-label">已下架</div>
              </div>
            </div>
          </div>

          <!-- 工具栏 -->
          <div class="toolbar">
            <div class="toolbar-left">
              <div class="search-wrapper">
                <span class="search-icon">🔍</span>
                <input
                  v-model="goodsKeyword"
                  type="text"
                  placeholder="搜索商品名称..."
                  class="search-input-modern"
                  @keyup.enter="loadGoods(1)"
                />
                <button class="search-btn" @click="loadGoods(1)">搜索</button>
              </div>
            </div>
            <div class="toolbar-right">
              <span v-if="isSuper && goodsMerchantId > 0" class="merchant-filter-tip">
                当前筛选商家：{{ goodsMerchantName || ('#' + goodsMerchantId) }}
                <button class="clear-btn" @click="clearGoodsMerchantFilter">清除筛选</button>
              </span>
              <button class="add-btn" @click="goGoodsForm()">➕ 新增商品</button>
            </div>
          </div>

          <!-- 商品表格 -->
          <div class="table-wrapper" v-if="goodsList.length > 0">
            <table class="admin-table">
              <thead>
                <tr>
                  <th style="width: 70px">图片</th>
                  <th>商品名称</th>
                  <th style="width: 150px" v-if="isSuper">所属店铺</th>
                  <th style="width: 140px">价格</th>
                  <th style="width: 60px">库存</th>
                  <th style="width: 60px">销量</th>
                  <th style="width: 85px">状态</th>
                  <th style="width: 140px">首页展示</th>
                  <th style="width: 210px">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in goodsList" :key="row.goods_id">
                  <td>
                    <img :src="row.goods_image || defaultImage" class="table-img" alt="" />
                  </td>
                  <td>
                    <div class="goods-name-cell">
                      <div class="name-main">{{ row.goods_name }}</div>
                      <div class="name-sub" v-if="row.goods_subtitle">{{ row.goods_subtitle }}</div>
                      <div class="sku-badge" v-if="row.sku_count > 0">已设 {{ row.sku_count }} 个规格</div>
                      <div class="sku-badge group-badge" v-if="row.is_group_buy === 1">拼单 ¥{{ row.group_price }} / {{ row.group_num }}人</div>
                    </div>
                  </td>
                  <td v-if="isSuper">
                    <span class="shop-name-text">{{ row.shop_name || '-' }}</span>
                  </td>
                  <td>
                    <span class="price-text">
                      ¥{{ row.goods_price_min }}
                      <span v-if="row.goods_price_max > row.goods_price_min"> - ¥{{ row.goods_price_max }}</span>
                    </span>
                  </td>
                  <td>{{ row.goods_stock }}</td>
                  <td>{{ row.goods_sales }}</td>
                  <td>
                    <span class="status-tag" :class="row.is_on_sale === 1 ? 'on-sale' : 'off-sale'">
                      {{ row.is_on_sale === 1 ? '已上架' : '已下架' }}
                    </span>
                  </td>
                  <td>
                    <div class="home-switch-wrapper">
                      <div 
                        class="custom-switch" 
                        :class="{ active: row.show_on_home === 1 }"
                        @click="toggleHome(row)"
                      >
                        <div class="switch-track">
                          <div class="switch-thumb"></div>
                        </div>
                      </div>
                      <span class="switch-label" :class="row.show_on_home === 1 ? 'show' : 'hide'">
                        {{ row.show_on_home === 1 ? '显示' : '隐藏' }}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div class="action-btns">
                      <button class="action-btn edit" @click="goGoodsForm(row.goods_id)">编辑</button>
                      <button class="action-btn" :class="row.is_on_sale === 1 ? 'off' : 'on'" @click="toggleStatus(row)">
                        {{ row.is_on_sale === 1 ? '下架' : '上架' }}
                      </button>
                      <button class="action-btn delete" @click="deleteGoods(row)">删除</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="table-footer">
              <span class="total-info">共 {{ goodsTotalPage }} 页 / {{ goodsList.length }} 条</span>
              <div class="pagination">
                <button class="page-btn" :disabled="goodsPage <= 1" @click="onGoodsPageChange(goodsPage - 1)">上一页</button>
                <span class="page-num">{{ goodsPage }} / {{ goodsTotalPage || 1 }}</span>
                <button class="page-btn" :disabled="goodsPage >= goodsTotalPage" @click="onGoodsPageChange(goodsPage + 1)">下一页</button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <span style="font-size:48px;">📭</span>
            <p>暂无商品</p>
          </div>
        </div>

        <!-- ============ 订单管理 ============ -->
        <div v-show="activeTab === 'orders'" class="tab-panel">
          <!-- 统计卡片 -->
          <div class="stats-grid">
            <div class="stat-card" v-for="stat in orderStats" :key="stat.key">
              <div class="stat-icon" :class="stat.iconClass">{{ stat.icon }}</div>
              <div class="stat-info">
                <div class="stat-value">{{ stat.count }}</div>
                <div class="stat-label">{{ stat.label }}</div>
              </div>
            </div>
          </div>

          <!-- 订单筛选 -->
          <div class="order-filter-bar">
            <div class="filter-tabs-modern">
              <button
                class="filter-tab"
                v-for="tab in orderTabs"
                :key="tab.key"
                :class="{ active: orderType === tab.key }"
                @click="switchOrderTab(tab.key)"
              >
                {{ tab.label }}
                <span class="tab-count">{{ statCount(tab.key) }}</span>
              </button>
            </div>
            <div class="filter-actions">
              <div class="order-search-wrapper">
                <span class="search-icon">🔍</span>
                <input
                  v-model="orderKeyword"
                  type="text"
                  placeholder="搜索订单号/手机号/昵称..."
                  class="order-search-input"
                  @keyup.enter="searchOrders"
                />
                <button class="search-btn" @click="searchOrders">搜索</button>
                <button v-if="orderKeyword" class="reset-btn" @click="resetOrderSearch">重置</button>
              </div>

              <button
                v-if="pendingOrders.length > 0"
                class="batch-btn"
                @click="batchShip"
              >
                📦 批量发货 ({{ pendingOrders.length }})

              </button>
            </div>
          </div>

          <!-- 订单列表 -->
          <div class="order-list" v-if="filteredOrderList.length > 0">
            <div class="order-card" v-for="order in filteredOrderList" :key="order.order_id">
              <div class="order-header">
                <div class="order-info">
                  <span class="order-no">📋 {{ order.order_no }}</span>
                  <span class="order-time">{{ order.create_time || order.pay_time || '' }}</span>
                </div>
                <div class="order-buyer">
                  <span class="buyer-item buyer-mobile">👤 {{ order.user_mobile || '未绑定手机号' }}</span>
                  <span class="buyer-item buyer-nick">{{ order.user_nickname || '' }}</span>
                </div>
                <span class="status-tag" :class="getOrderStatusClass(order.state_text)">
                  {{ order.state_text }}
                </span>
              </div>
              <div class="order-body">
                <div class="order-goods-list">
                  <div class="order-goods-item" v-for="g in order.goods" :key="g.goods_id">
                    <img :src="g.goods_image" alt="" />
                    <span class="goods-name">
                      {{ g.goods_name }}
                      <span class="goods-spec-text" v-if="g.spec_text">{{ g.spec_text }}</span>
                    </span>
                    <span class="goods-qty">×{{ g.total_num }}</span>
                  </div>
                </div>
                <div class="order-summary">
                  <span class="total-qty">共 {{ order.goods_total_num }} 件</span>
                  <span class="total-price">¥{{ order.total_price }}</span>
                </div>
              </div>
              <!-- 待支付：只显示取消按钮 -->
              <div class="order-footer" v-if="order.state_text === '待支付'">
                <button class="cancel-btn" @click="cancelOrder(order)">取消订单</button>
              </div>
              <!-- 待发货：显示发货和取消按钮 -->
              <div class="order-footer" v-else-if="order.state_text === '待发货'">
                <button class="ship-btn" @click="shipOrder(order)">🚚 发货</button>
                <button class="cancel-btn" @click="cancelOrder(order)">立即退款</button>
              </div>
              <!-- 待收货：显示确认收货按钮 -->
              <div class="order-footer" v-else-if="order.state_text === '待收货'">
                <button class="confirm-receive-btn" @click="confirmReceive(order)">✅ 确认收货</button>
              </div>
              <!-- 退款中：显示同意/拒绝退款 -->
              <div class="order-footer" v-else-if="order.state_text === '退款中'">
                <div class="refund-reason" v-if="order.refund">退款申请：¥{{ order.refund.amount }}；原因：{{ order.refund.reason }}</div>
                <button class="refund-reject-btn" @click="rejectRefund(order)">拒绝退款</button>
                <button class="refund-approve-btn" @click="approveRefund(order)">✅ 同意退款</button>
              </div>

            </div>
            <div class="load-more" v-if="orderTotalPage > orderPage">
              <button class="load-more-btn" @click="loadOrders(orderPage + 1)">加载更多</button>
            </div>
          </div>
          <div v-else class="empty-state">
            <span style="font-size:48px;">📭</span>
            <p>暂无订单</p>
          </div>
        </div>

        <!-- ============ 数据分析 ============ -->
        <div v-show="activeTab === 'stats'" class="tab-panel">
          <!-- 日期筛选工具栏 -->
          <div class="toolbar stats-toolbar">
            <div class="stats-filter">
              <label>开始日期</label>
              <input v-model="statsStartDate" type="date" />
              <span class="date-sep">至</span>
              <label>结束日期</label>
              <input v-model="statsEndDate" type="date" />
              <button class="search-btn" @click="loadStats">📊 查询</button>
              <button class="reset-btn" @click="resetStatsDate">🔄 今天</button>
            </div>
          </div>

          <!-- 统计卡片 -->
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-icon stat-all">📋</div>
              <div class="stat-info">
                <div class="stat-value">{{ statsData.order_count }}</div>
                <div class="stat-label">订单数量（单）</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon sales-icon">💰</div>
              <div class="stat-info">
                <div class="stat-value stat-money">￥{{ formatSales(statsData.sales_amount) }}</div>
                <div class="stat-label">销售额（元）</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon visitor-icon">👤</div>
              <div class="stat-info">
                <div class="stat-value">{{ statsData.visitor_count }}</div>
                <div class="stat-label">商品访客数（人）</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon view-icon">👁️</div>
              <div class="stat-info">
                <div class="stat-value">{{ statsData.view_count }}</div>
                <div class="stat-label">商品浏览量（次）</div>
              </div>
            </div>
            <div class="stat-card">
              <div class="stat-icon comment-icon">⭐</div>
              <div class="stat-info">
                <div class="stat-value">{{ statsData.comment_count }}</div>
                <div class="stat-label">商品评价数（条）</div>
              </div>
            </div>
          </div>

          <!-- 查询范围提示 -->
          <div class="stats-date-tip">📅 查询范围：{{ statsData.start_date || statsStartDate }} 至 {{ statsData.end_date || statsEndDate }}</div>

          <!-- 商品数据分析（需求 5）：逐商品订单数 / 销售金额 / 访客数 / 浏览量 / 评价数 -->
          <div class="goods-stats-block">
            <div class="block-title">
              📦 商品数据分析
              <span class="block-sub">共 {{ (statsData.goods_list || []).length }} 个商品（按订单数排序）</span>
            </div>
            <div class="table-wrapper" v-if="(statsData.goods_list || []).length > 0">
              <table class="admin-table">
                <thead>
                  <tr>
                    <th style="width: 60px">图片</th>
                    <th>商品名称</th>
                    <th style="width: 150px" v-if="isSuper">所属店铺</th>
                    <th style="width: 90px">订单数量</th>
                    <th style="width: 120px">销售金额</th>
                    <th style="width: 100px">商品访客数</th>
                    <th style="width: 100px">商品浏览量</th>
                    <th style="width: 100px">商品评价数</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in statsData.goods_list" :key="row.goods_id">
                    <td>
                      <img :src="row.goods_image || defaultImage" class="gs-img" alt="">
                    </td>
                    <td>
                      <div class="gs-name">{{ row.goods_name }}</div>
                      <div class="gs-sub">¥{{ row.goods_price_min }} · 累计销量 {{ row.goods_sales }}</div>
                    </td>
                    <td v-if="isSuper">{{ row.shop_name || '-' }}</td>
                    <td>{{ row.order_count }}</td>
                    <td class="gs-money">￥{{ formatSales(row.sales_amount) }}</td>
                    <td>{{ row.visitor_count }}</td>
                    <td>{{ row.view_count }}</td>
                    <td>{{ row.comment_count }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="empty-state">
              <span style="font-size: 40px;">📭</span>
              <p>该时间范围内暂无商品经营数据</p>
            </div>
          </div>
        </div>

        <!-- ============ 商城数据（超级管理员） ============ -->
        <div v-if="isSuper" v-show="activeTab === 'overview'" class="tab-panel">
          <shop-overview />
        </div>

        <!-- ============ 商家管理（超级管理员） ============ -->
        <div v-if="isSuper" v-show="activeTab === 'merchants'" class="tab-panel">
          <merchant-panel @view-goods="onViewMerchantGoods" />
        </div>

        <!-- ============ 搜索分析（需求 1） ============ -->
        <div v-show="activeTab === 'search'" class="tab-panel">
          <search-analysis />
        </div>

        <!-- ============ 资金中心（商家货款，需求 6） ============ -->
        <div v-if="!isSuper" v-show="activeTab === 'fund'" class="tab-panel">
          <fund-center />
        </div>

        <!-- ============ 店铺资料 / 商家资质（普通商家） ============ -->
        <div v-if="!isSuper" v-show="activeTab === 'shop'" class="tab-panel">
          <shop-profile />
        </div>
      </div>

        <!-- ============ 图片空间 ============ -->
        <div v-show="activeTab === 'images'" class="tab-panel">
          <div class="toolbar">
            <div class="toolbar-left">
              <span class="toolbar-title">共 {{ imageTotal }} 张图片</span>
              <span class="toolbar-tip">{{ isSuper ? '（全部商家的图片）' : '（仅本店铺上传的图片）' }}</span>
            </div>
            <div class="toolbar-right">
              <input ref="imageFileInput" type="file" accept="image/*" style="display:none" @change="handleImageUpload" />
              <button class="add-btn" :disabled="imageUploading" @click="$refs.imageFileInput.click()">
                {{ imageUploading ? '上传中...' : '📤 上传图片' }}
              </button>
            </div>
          </div>

          <div class="image-grid" v-if="imageList.length > 0">
            <div class="image-card" v-for="img in imageList" :key="img.id">
              <img :src="img.image_url" alt="" />
              <div class="image-info">
                <span class="image-name" :title="img.file_name">{{ img.file_name }}</span>
                <span class="image-time">{{ img.created_at }}</span>
                <span class="image-uploader" v-if="isSuper && img.uploader">来自：{{ img.uploader }}</span>
              </div>
              <div class="image-actions">
                <button class="image-use-btn" @click="copyImageUrl(img)">复制链接</button>
                <button class="image-del-btn" @click="deleteImage(img)">删除</button>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">
            <span style="font-size:48px;">🖼️</span>
            <p>暂无图片，点击右上角「上传图片」</p>
          </div>

          <div class="table-footer" v-if="imageTotalPage > 1">
            <span class="total-info">共 {{ imageTotalPage }} 页</span>
            <div class="pagination">
              <button class="page-btn" :disabled="imagePage <= 1" @click="loadImageLibrary(imagePage - 1)">上一页</button>
              <span class="page-num">{{ imagePage }} / {{ imageTotalPage }}</span>
              <button class="page-btn" :disabled="imagePage >= imageTotalPage" @click="loadImageLibrary(imagePage + 1)">下一页</button>
            </div>
          </div>
        </div>

        <!-- ============ 系统设置 ============ -->
        <div v-show="activeTab === 'settings'" class="tab-panel">
          <!-- 支付方式设置：仅超级管理员可配置（普通商家系统设置仅保留修改密码） -->
          <div class="settings-card" v-if="isSuper">
            <div class="settings-card-title">💳 支付方式设置</div>
            <div class="settings-card-desc">开启后，商城结算页即可选择对应的支付方式；关闭后该支付方式在商城不可用。</div>
            <div class="settings-row">
              <div class="settings-label">
                <span class="method-icon wechat">微</span>
                <div class="settings-text">
                  <div class="settings-name">微信支付</div>
                  <div class="settings-desc">开启后商城结算可选择微信支付</div>
                </div>
              </div>
              <div class="custom-switch" :class="{ active: settings.payment_wechat_enabled }" @click="toggleSetting('payment_wechat_enabled')">
                <div class="switch-track"><div class="switch-thumb"></div></div>
              </div>
            </div>
            <div class="settings-row">
              <div class="settings-label">
                <span class="method-icon alipay">支</span>
                <div class="settings-text">
                  <div class="settings-name">支付宝支付</div>
                  <div class="settings-desc">开启后商城结算可选择支付宝支付</div>
                </div>
              </div>
              <div class="custom-switch" :class="{ active: settings.payment_alipay_enabled }" @click="toggleSetting('payment_alipay_enabled')">
                <div class="switch-track"><div class="switch-thumb"></div></div>
              </div>
            </div>
            <div class="settings-row">
              <div class="settings-label">
                <span class="method-icon balance">余</span>
                <div class="settings-text">
                  <div class="settings-name">余额支付</div>
                  <div class="settings-desc">开启后商城结算可选择账户余额支付（余额自动退回原路）</div>
                </div>
              </div>
              <div class="custom-switch" :class="{ active: settings.payment_balance_enabled }" @click="toggleSetting('payment_balance_enabled')">
                <div class="switch-track"><div class="switch-thumb"></div></div>
              </div>
            </div>
            <div class="settings-actions">
              <button class="modal-btn confirm" :disabled="settingsSaving" @click="saveSettings">{{ settingsSaving ? '保存中...' : '保存设置' }}</button>
            </div>
          </div>

          <!-- 修改密码（超级管理员 / 普通商家均可修改自己的登录密码） -->
          <div class="settings-card">
            <div class="settings-card-title">🔑 修改密码</div>
            <div class="form-group">
              <label>当前用户名</label>
              <input :value="adminUsername" disabled placeholder="管理员用户名" />
            </div>
            <div class="form-group">
              <label>旧密码 <span class="required">*</span></label>
              <input v-model="changePasswordForm.old_password" type="password" placeholder="请输入当前密码" />
            </div>
            <div class="form-group">
              <label>新密码 <span class="required">*</span></label>
              <input v-model="changePasswordForm.new_password" type="password" placeholder="至少 6 位" />
            </div>
            <div class="form-group">
              <label>确认新密码 <span class="required">*</span></label>
              <input v-model="changePasswordForm.confirm_password" type="password" placeholder="请再次输入新密码" />
            </div>
            <p class="form-tip">提示：修改成功后，当前登录会话保持有效，下次登录请使用新密码。</p>
            <div class="settings-actions">
              <button class="modal-btn confirm" @click="submitChangePassword">修改密码</button>
            </div>
          </div>
        </div>

        <!-- ============ 百度收录（普通收录 API 主动推送） ============ -->
        <div v-show="activeTab === 'seo'" class="tab-panel">
          <div class="settings-card">
            <div class="settings-card-title">🔍 百度收录推送状态</div>
            <div class="settings-card-desc">
              通过百度搜索资源平台「普通收录 - API 提交」主动推送网站 URL，加快百度收录网站及网站内容。
              新增商品、编辑更新商品、商品上架、网站内容变化时都会自动推送（响应返回后异步提交，不影响后台操作速度）；
              同一 URL 当天只自动推送一次，配额用尽时自动跳过，避免浪费百度每日配额。
            </div>
            <div class="seo-info-grid">
              <div class="seo-info-item">
                <span class="seo-info-label">站点</span>
                <span class="seo-info-value">{{ seoStatus ? seoStatus.site : '-' }}</span>
              </div>
              <div class="seo-info-item">
                <span class="seo-info-label">准入密钥</span>
                <span class="seo-info-value">{{ seoStatus ? seoStatus.token : '-' }}</span>
              </div>
              <div class="seo-info-item">
                <span class="seo-info-label">URL 形式</span>
                <span class="seo-info-value">{{ seoStatus && seoStatus.url_mode === 'hash' ? 'hash 路由（/#/prodetail/12）' : '伪静态（/prodetail/12）' }}</span>
              </div>
              <div class="seo-info-item">
                <span class="seo-info-label">推送开关</span>
                <span class="seo-info-value" :class="{ off: seoStatus && !seoStatus.enabled }">{{ seoStatus && seoStatus.enabled ? '已开启' : '已关闭（baidu_config.php）' }}</span>
              </div>
              <div class="seo-info-item">
                <span class="seo-info-label">剩余配额</span>
                <span class="seo-info-value">{{ seoRemainText }}</span>
              </div>
              <div class="seo-info-item">
                <span class="seo-info-label">今日已提交</span>
                <span class="seo-info-value">{{ seoStatus ? seoStatus.sent_today + ' 个 URL（含手动推送）' : '-' }}</span>
              </div>
              <div class="seo-info-item">
                <span class="seo-info-label">上次推送</span>
                <span class="seo-info-value">{{ seoStatus && seoStatus.last ? (seoStatus.last.time + '（' + seoStatus.last.source + '，成功 ' + seoStatus.last.success + ' 条）') : '暂无记录' }}</span>
              </div>
            </div>
            <div class="seo-url-tip" v-if="seoStatus && seoStatus.samples">
              示例地址：{{ seoStatus.samples.home }} ｜ {{ seoStatus.samples.goods }} ｜ {{ seoStatus.samples.category }}
            </div>
            <div class="settings-actions">
              <button class="modal-btn" :disabled="seoPushing" @click="loadSeoStatus">刷新状态</button>
              <button class="modal-btn confirm" :disabled="seoPushing" @click="pushHome">推送首页</button>
              <button class="modal-btn confirm" :disabled="seoPushing" @click="pushAllGoods">{{ seoPushing ? '推送中...' : '推送全部商品 + 分类' }}</button>
            </div>
          </div>

          <div class="settings-card">
            <div class="settings-card-title">🚀 手动推送自定义 URL</div>
            <div class="settings-card-desc">
              每行一个 URL，可填完整地址（{{ seoStatus ? seoStatus.site : '' }}/prodetail/12）或站内相对路径（prodetail/12、searchlist?categoryId=1）。
            </div>
            <textarea v-model="seoCustomUrls" class="seo-textarea" rows="5" placeholder="prodetail/12&#10;searchlist?categoryId=1"></textarea>
            <div class="settings-actions">
              <button class="modal-btn confirm" :disabled="seoPushing" @click="pushCustomUrls">{{ seoPushing ? '推送中...' : '立即推送' }}</button>
            </div>
          </div>

          <div class="settings-card">
            <div class="settings-card-title">🗺️ 站点地图（sitemap.xml）</div>
            <div class="settings-card-desc">
              动态生成「首页 + 分类列表页 + 全部上架商品详情页」的 sitemap，配合主动推送可显著提升收录效率。
              其访问地址需服务器把 <code>/sitemap.xml</code> 重写到 <code>/server/sitemap.php</code>（配置见 README）。
            </div>
            <div class="seo-url-tip">
              sitemap：{{ seoStatus ? seoStatus.site : '' }}/sitemap.xml ｜ 独立入口：{{ seoStatus ? seoStatus.site : '' }}/server/sitemap.php ｜ 接口：{{ seoStatus ? seoStatus.site : '' }}/api/sitemap/index
            </div>
            <div class="settings-actions">
              <button class="modal-btn" :disabled="sitemapLoading" @click="loadSitemapUrls">{{ sitemapLoading ? '加载中...' : '预览 sitemap URL 数量' }}</button>
              <button class="modal-btn" @click="openSitemap">打开 sitemap.xml</button>
            </div>
            <div class="seo-result-detail" v-if="sitemapTotal !== null">
              sitemap 当前包含 {{ sitemapTotal }} 条 URL{{ sitemapTotal > 0 ? '（前 20 条：' + sitemapSamples.join('、') + '）' : '' }}
            </div>
          </div>

          <div class="settings-card" v-if="seoResult">
            <div class="settings-card-title">📣 本次推送结果</div>
            <div class="seo-result" :class="{ fail: !seoResult.ok }">{{ seoResult.message }}</div>
            <div class="seo-result-detail">
              提交 {{ seoResult.count }} 条，成功 {{ seoResult.success }} 条，耗时 {{ seoResult.cost_ms }}ms
              <template v-if="seoResult.not_valid && seoResult.not_valid.length">，URL 不合法 {{ seoResult.not_valid.length }} 条</template>
              <template v-if="seoResult.not_same_site && seoResult.not_same_site.length">，非本站 {{ seoResult.not_same_site.length }} 条</template>
            </div>
          </div>

          <div class="settings-card">
            <div class="settings-card-title">
              📄 最近推送日志
              <span class="seo-log-file" v-if="seoStatus">{{ seoStatus.log_file }}</span>
            </div>
            <div class="seo-log-list" v-if="seoLogs.length > 0">
              <div class="seo-log-item" v-for="(line, idx) in seoLogs" :key="idx">{{ line }}</div>
            </div>
            <div v-else class="empty-state">
              <span style="font-size:40px;">📭</span>
              <p>暂无推送日志</p>
            </div>
            <div class="settings-actions">
              <button class="modal-btn" :disabled="seoLogLoading" @click="loadSeoLog">{{ seoLogLoading ? '加载中...' : '刷新日志' }}</button>
            </div>
          </div>
        </div>
      </main>

    <!-- ============ 管理后台登录遮罩 ============ -->
    <AdminLogin
      v-if="!adminAuthed"
      :loading="adminLoginLoading"
      :captcha-img="adminCaptchaImg"
      :captcha-key="adminCaptchaKey"
      @login="handleAdminLogin"
      @refresh-captcha="loadAdminCaptcha"
    />

  </div>
</template>

<script>
import AdminLogin from './LoginModal.vue'
import ShopOverview from './ShopOverview.vue'
import MerchantPanel from './MerchantPanel.vue'
import SearchAnalysis from './SearchAnalysis.vue'
import FundCenter from './FundCenter.vue'
import ShopProfile from './ShopProfile.vue'
import { getMerchantInfoApi } from '@/api/merchant'
import {
  getAdminGoodsListApi,
  getAdminGoodsDetailApi,
  addGoodsApi,
  editGoodsApi,
  toggleGoodsStatusApi,
  deleteGoodsApi,
  toggleHomeGoodsApi,
  getAdminOrderListApi,
  getAdminOrderStatsApi,
  shipOrderApi,
  cancelOrderApi,
  batchShipOrderApi,
  confirmOrderApi,
  handleRefundApi,
  getStatsOverviewApi,
  adminLoginApi,
  adminCheckAuthApi,
  adminChangePasswordApi,
  adminLogoutApi,
  getAdminCaptchaApi,
  uploadImageLibraryApi,
  getImageLibraryApi,
  deleteImageLibraryApi,
  getSettingsApi,
  saveSettingsApi,
  getBaiduPushStatusApi,
  baiduPushApi,
  getBaiduPushLogApi,
  getSitemapUrlsApi,
  saveAutoDeliveryApi,
  getGoodsCardsApi
} from '@/api/admin'

export default {
  name: 'Admin',
  components: { AdminLogin, ShopOverview, MerchantPanel, SearchAnalysis, FundCenter, ShopProfile },
  data () {
    return {
      defaultImage: 'https://img.yzcdn.cn/vant/cat.png',
      activeTab: 'goods',
      // 角色信息（超级管理员 / 普通商家）：由 /merchant/info 接口获取，决定左侧菜单
      adminRole: 1,
      shopName: '',
      // 商品管理：超级管理员可按商家筛选（如从「商家管理」点击“商品”）
      goodsMerchantId: 0,
      goodsMerchantName: '',
      goodsList: [],
      goodsPage: 1,
      goodsTotalPage: 1,
      goodsKeyword: '',
      orderList: [],
      orderPage: 1,
      orderTotalPage: 1,
      orderType: 'all',
      orderKeyword: '',

      orderTabs: [
        { key: 'all', label: '全部' },
        { key: 'payment', label: '待支付' },
        { key: 'groupwaiting', label: '待成团' },
        { key: 'delivery', label: '待发货' },
        { key: 'received', label: '待收货' },
        { key: 'comment', label: '已完成' },
        { key: 'refund', label: '退款中' },
        { key: 'cancel', label: '已退款' }
      ],
      statsStartDate: '',
      statsEndDate: '',
      statsData: {
        start_date: '',
        end_date: '',
        order_count: 0,
        sales_amount: 0,
        visitor_count: 0,
        view_count: 0,
        comment_count: 0
      },
      adminAuthed: false,
      adminLoginLoading: false,
      adminUsername: '',
      adminCaptchaImg: '',
      adminCaptchaKey: '',
      showChangePassword: false,
      changePasswordForm: {
        old_password: '',
        new_password: '',
        confirm_password: ''
      },
      orderStatsData: null,
      // 图片空间
      imageList: [],
      imagePage: 1,
      imageTotalPage: 1,
      imageTotal: 0,
      imageUploading: false,
      // 系统设置
      settings: {
        payment_wechat_enabled: 1,
        payment_alipay_enabled: 1,
        payment_balance_enabled: 1
      },
      settingsSaving: false,
      // 百度收录（主动推送）
      seoStatus: null,
      seoLogs: [],
      seoLogLoading: false,
      seoPushing: false,
      seoCustomUrls: '',
      seoResult: null,
      // SEO 站点地图
      sitemapLoading: false,
      sitemapTotal: null,
      sitemapSamples: []
    }
  },
  computed: {
    // 是否超级管理员（决定左侧菜单与数据范围）
    isSuper () {
      return Number(this.adminRole) === 2
    },
    // 左侧菜单：超级管理员 = 商城管理（商家/商家商品/商城数据/搜索分析/支付设置/百度收录）；
    //           普通商家  = 商品管理 / 订单管理 / 资金中心 / 数据分析 / 搜索分析 / 图片空间 / 系统设置（仅改密码）
    tabs () {
      if (this.isSuper) {
        return [
          { key: 'overview', label: '商城数据', icon: '📈' },
          { key: 'merchants', label: '商家管理', icon: '🏪' },
          { key: 'goods', label: '商品管理', icon: '📦' },
          { key: 'orders', label: '订单管理', icon: '📋' },
          { key: 'stats', label: '数据分析', icon: '📊' },
          { key: 'search', label: '搜索分析', icon: '🔍' },
          { key: 'settings', label: '支付方式设置', icon: '💳' },
          { key: 'seo', label: '百度收录', icon: '🌐' },
          { key: 'service', label: '客服工作台', icon: '💬' }
        ]
      }
      return [
        { key: 'goods', label: '商品管理', icon: '📦' },
        { key: 'orders', label: '订单管理', icon: '📋' },
        { key: 'fund', label: '资金中心', icon: '💰' },
        { key: 'stats', label: '数据分析', icon: '📊' },
        { key: 'search', label: '搜索分析', icon: '🔍' },
        { key: 'images', label: '图片空间', icon: '🖼️' },
        { key: 'shop', label: '店铺资料', icon: '🏪' },
        { key: 'settings', label: '系统设置', icon: '⚙️' },
        { key: 'service', label: '客服工作台', icon: '💬' }
      ]
    },
    currentTabLabel () {
      const tab = this.tabs.find(t => t.key === this.activeTab);
      return tab ? tab.label : ""
    },    orderStats () {
      // 顶部统计卡优先使用后端全量统计（避免分页截断），未加载成功时回退本地统计
      const s = this.orderStatsData || {};
      const cnt = (k, local) => (s[k] !== undefined ? s[k] : local);
      const all = this.orderList;
      return [
        { key: "all", icon: "📊", iconClass: "stat-all", count: cnt('total', all.length), label: "全部订单" },
        { key: "payment", icon: "⏳", iconClass: "stat-pending", count: cnt('payment', all.filter(o => o.state_text === "待支付").length), label: "待支付" },
        { key: "groupwaiting", icon: "🧑‍🤝‍🧑", iconClass: "stat-pending", count: cnt('groupwaiting', all.filter(o => o.state_text === "待成团").length), label: "待成团" },
        { key: "delivery", icon: "📦", iconClass: "stat-pending", count: cnt('delivery', all.filter(o => o.state_text === "待发货").length), label: "待发货" },
        { key: "done", icon: "✅", iconClass: "stat-done", count: cnt('done', all.filter(o => o.state_text === "已完成").length), label: "已完成" },
        { key: "refund", icon: "🔁", iconClass: "stat-refund", count: cnt('refund', all.filter(o => o.state_text === "退款中").length), label: "退款中" },
        { key: "cancel", icon: "❌", iconClass: "stat-cancel", count: cnt('cancel', all.filter(o => o.state_text === "已取消" || o.state_text === "已退款").length), label: "已退款" }
      ];
    },
    pendingOrders () {
      return this.orderList.filter(o => o.state_text === "待发货" || o.state_text === "待支付");
    },
    // 百度收录：剩余配额展示文案（推送成功后由接口返回）
    seoRemainText () {
      const last = this.seoStatus && this.seoStatus.last;
      if (!last || last.remain === null || last.remain === undefined) {
        return "未知（推送一次后显示）";
      }
      return last.remain + " 条";
    },
    filteredOrderList () {
      // 列表已按当前类型（后端 dataType：all/payment/groupwaiting/delivery/received/comment/refund/cancel）
      // 从服务端分页返回，直接展示即可，避免本地按状态文本二次过滤造成不一致（如拼单退款中订单）
      return this.orderList
    }
  },

  created () {
    this.applyRouteQuery()
    this.checkAdminAuth()
  },
  watch: {
    // 从「商品新增/编辑」独立页面返回时（?tab=goods&refresh=xxx）刷新商品列表
    '$route.query.refresh' () {
      if (this.$route.query.refresh) {
        this.applyRouteQuery()
        this.loadGoods(1)
      }
    }
  },
  methods: {
    // 支持通过 URL 参数定位到指定 Tab / 触发列表刷新（供独立商品页返回时使用）
    applyRouteQuery () {
      const tab = this.$route.query.tab
      if (tab && this.tabs.some(t => t.key === tab)) {
        this.activeTab = tab
      }
      if (this.$route.query.refresh && this.activeTab === 'goods' && this.adminAuthed) {
        this.loadGoods(1)
      }
    },
    getOrderCount (key) {
      if (key === 'all') return this.orderList.length
      const map = {
        'payment': '待支付',
        'groupwaiting': '待成团',
        'delivery': '待发货',
        'received': '待收货',
        'comment': '已完成',
        'refund': '退款中',
        'cancel': ['已取消', '已退款']
      }
      const targets = map[key]
      if (Array.isArray(targets)) {
        return this.orderList.filter(o => targets.includes(o.state_text)).length
      }
      return this.orderList.filter(o => o.state_text === targets).length
    },
    // tab 计数：优先后端全量统计（各状态独立分页时依然准确）
    statCount (key) {
      const s = this.orderStatsData || {}
      const field = key === 'all' ? 'total' : key
      if (s[field] !== undefined) return s[field]
      return this.getOrderCount(key)
    },
    getOrderStatusClass (text) {
      const map = {
        '待支付': 'status-pending',
        '待发货': 'status-shipped',
        '待收货': 'status-received',
        '已完成': 'status-done',
        '退款中': 'status-refund',
        '已取消': 'status-cancel',
        '已退款': 'status-refund',
        '待成团': 'status-pending',
        '拼团失败': 'status-cancel'
      }
      return map[text] || 'status-default'
    },
    switchTab (key) {
      // 「客服工作台」不是内容面板，而是打开对应账号的客服工作台（商家 / 官方客服）
      if (key === 'service') {
        this.openAgentWorkbench()
        return
      }
      this.activeTab = key
      if (key === 'orders' && this.orderList.length === 0) {
        this.loadOrders(1)
      }
      if (key === 'stats') {
        this.loadStats()
      }
      if (key === 'images') {
        this.loadImageLibrary(1)
      }
      if (key === 'settings') {
        this.loadSettings()
      }
      if (key === 'seo') {
        this.loadSeoStatus()
        this.loadSeoLog()
      }
    },
    // ---------- 管理后台登录 ----------
    // 应用角色信息（超级管理员 / 普通商家）：决定左侧菜单与数据范围
    applyRoleInfo (data) {
      const info = data || {}
      if (info.role !== undefined) {
        this.adminRole = Number(info.role) || 1
      }
      this.shopName = info.shop_name || ''
      // 兜底：会话信息接口（含 menus）补充角色判断
      if (info.menus && Array.isArray(info.menus)) {
        this.adminRole = info.menus.includes('merchants') ? 2 : 1
      }
      // 角色切换后确保当前面板属于该角色菜单
      if (!this.tabs.some(t => t.key === this.activeTab)) {
        this.activeTab = this.tabs[0].key
      }
    },
    // 重新从服务端拉取角色信息（登录信息接口最完整）
    refreshRoleInfo () {
      return getMerchantInfoApi()
        .then((res) => {
          this.applyRoleInfo(res.data)
        })
        .catch((e) => {
          console.error('获取商家信息失败', e)
        })
    },
    // 打开客服工作台（商家 → 自己的店铺客服；超级管理员 → 官方客服）
    openAgentWorkbench () {
      const tip = this.isSuper
        ? '客服工作台用于接待「商城官方客服」会话。\n请使用管理员账号登录桌面客服工作台（客服工作台.exe），或在本机 9501 端口服务已启动时使用。'
        : '客服工作台用于接待本店铺的买家会话。\n请使用商家账号登录桌面客服工作台（客服工作台.exe）。'
      alert(tip)
    },
    // 从「商家管理」查看该商家的商品：切换商品管理面板并按商家筛选
    onViewMerchantGoods (row) {
      this.goodsMerchantId = Number(row.id) || 0
      this.goodsMerchantName = row.shop_name || ''
      this.activeTab = 'goods'
      this.loadGoods(1)
    },
    clearGoodsMerchantFilter () {
      this.goodsMerchantId = 0
      this.goodsMerchantName = ''
      this.loadGoods(1)
    },
    loadAdminCaptcha () {
      getAdminCaptchaApi()
        .then((res) => {
          if (res.data) {
            this.adminCaptchaImg = res.data.img
            this.adminCaptchaKey = res.data.key
          }
        })
        .catch((e) => {
          console.error('获取验证码失败', e)
        })
    },
    checkAdminAuth () {
      const token = window.localStorage.getItem('admin_token')
      if (!token) {
        this.adminAuthed = false
        this.loadAdminCaptcha()
        return
      }
      adminCheckAuthApi()
        .then((res) => {
          this.adminAuthed = true
          this.adminUsername = (res.data && res.data.username) || ''
          this.applyRoleInfo(res.data)
          this.loadGoods(1)
          this.initStatsDate()
          this.loadStats()
        })
        .catch((e) => {
          console.error('管理员登录状态校验失败', e)
          window.localStorage.removeItem('admin_token')
          this.adminAuthed = false
          this.loadAdminCaptcha()
        })
    },
    handleAdminLogin ({ username, password, captchaCode, captchaKey }) {
      this.adminLoginLoading = true
      adminLoginApi(username, password, captchaCode, captchaKey)
        .then((res) => {
          const token = res.data && res.data.token
          if (!token) {
            throw new Error('登录响应异常')
          }
          window.localStorage.setItem('admin_token', token)
          this.adminUsername = (res.data && res.data.username) || username
          this.applyRoleInfo(res.data)
          this.adminAuthed = true
          this.loadGoods(1)
          this.initStatsDate()
          this.loadStats()
        })
        .catch((e) => {
          console.error('管理员登录失败', e)
          this.loadAdminCaptcha()
        })
        .finally(() => {
          this.adminLoginLoading = false
        })
    },
    adminLogout () {
      if (!confirm('确定退出管理后台吗？')) return
      adminLogoutApi()
        .catch((e) => {
          console.error('退出登录失败', e)
        })
        .finally(() => {
          window.localStorage.removeItem('admin_token')
          this.adminAuthed = false
          this.adminUsername = ''
          this.goodsList = []
          this.orderList = []
        })
    },
    submitChangePassword () {
      const f = this.changePasswordForm
      if (!f.old_password) {
        alert('请输入旧密码')
        return
      }
      if (!f.new_password) {
        alert('请输入新密码')
        return
      }
      if (f.new_password.length < 6) {
        alert('新密码长度不能少于 6 位')
        return
      }
      if (f.new_password !== f.confirm_password) {
        alert('两次输入的新密码不一致')
        return
      }
      adminChangePasswordApi(f.old_password, f.new_password)
        .then((res) => {
          if (res.data && res.data.token) {
            window.localStorage.setItem('admin_token', res.data.token)
          }
          alert('密码修改成功')
          this.changePasswordForm = {
            old_password: '',
            new_password: '',
            confirm_password: ''
          }
        })
        .catch((e) => {
          console.error('密码修改失败', e)
        })
    },
    // ---------- 图片空间 ----------
    loadImageLibrary (page) {
      getImageLibraryApi(page || 1)
        .then((res) => {
          const data = res.data || {}
          this.imageList = data.list || []
          this.imagePage = data.page || 1
          this.imageTotalPage = data.totalPage || 1
          this.imageTotal = data.total || 0
        })
        .catch((e) => {
          console.error('图片空间加载失败', e)
        })
    },
    handleImageUpload (e) {
      const file = e.target.files && e.target.files[0]
      if (!file) return
      this.uploadLibraryImage(file, () => {
        // 上传成功后刷新列表并重置 input
        this.$refs.imageFileInput.value = ''
        this.loadImageLibrary(this.imagePage)
      })
    },
    uploadLibraryImage (file, done) {
      this.imageUploading = true
      uploadImageLibraryApi(file)
        .then((res) => {
          alert('图片上传成功')
          if (done) done()
        })
        .catch((err) => {
          console.error('图片上传失败', err)
        })
        .finally(() => {
          this.imageUploading = false
        })
    },
    deleteImage (img) {
      if (!confirm('确定删除该图片吗？删除后无法恢复。')) return
      deleteImageLibraryApi(img.id)
        .then(() => {
          alert('删除成功')
          // 若当前页删空了且不是第一页，则回退一页
          if (this.imageList.length === 1 && this.imagePage > 1) {
            this.loadImageLibrary(this.imagePage - 1)
          } else {
            this.loadImageLibrary(this.imagePage)
          }
        })
        .catch((e) => {
          console.error('删除图片失败', e)
        })
    },
    copyImageUrl (img) {
      const text = img.image_url || ''
      const fallback = () => {
        window.prompt('复制图片链接：', text)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text)
          .then(() => alert('链接已复制：' + text))
          .catch(fallback)
      } else {
        fallback()
      }
    },
    // ---------- 系统设置 ----------
    loadSettings () {
      getSettingsApi()
        .then((res) => {
          const data = res.data || {}
          this.settings = {
            payment_wechat_enabled: data.payment_wechat_enabled !== undefined ? data.payment_wechat_enabled : 1,
            payment_alipay_enabled: data.payment_alipay_enabled !== undefined ? data.payment_alipay_enabled : 1,
            payment_balance_enabled: data.payment_balance_enabled !== undefined ? data.payment_balance_enabled : 1
          }
        })
        .catch((e) => {
          console.error('系统设置加载失败', e)
        })
    },
    toggleSetting (key) {
      this.settings[key] = this.settings[key] ? 0 : 1
    },
    saveSettings () {
      this.settingsSaving = true
      saveSettingsApi({
        payment_wechat_enabled: this.settings.payment_wechat_enabled,
        payment_alipay_enabled: this.settings.payment_alipay_enabled,
        payment_balance_enabled: this.settings.payment_balance_enabled
      })
        .then(() => {
          alert('设置保存成功')
        })
        .catch((e) => {
          console.error('设置保存失败', e)
        })
        .finally(() => {
          this.settingsSaving = false
        })
    },
    // ---------- 百度收录（主动推送） ----------
    // 推送状态：站点 / 准入密钥 / URL 形式 / 上次推送结果与剩余配额
    loadSeoStatus () {
      getBaiduPushStatusApi()
        .then((res) => {
          this.seoStatus = res.data || null
        })
        .catch((e) => {
          console.error('百度推送状态加载失败', e)
        })
    },
    // 最近推送日志
    loadSeoLog () {
      this.seoLogLoading = true
      getBaiduPushLogApi(30)
        .then((res) => {
          this.seoLogs = (res.data && res.data.list) || []
        })
        .catch((e) => {
          console.error('百度推送日志加载失败', e)
        })
        .finally(() => {
          this.seoLogLoading = false
        })
    },
    // 推送首页
    pushHome () {
      this.doBaiduPush({ type: 'home' })
    },
    // 全站推送：首页 + 全部分类列表页 + 全部上架商品详情页
    pushAllGoods () {
      const last = this.seoStatus && this.seoStatus.last
      const tip = last && last.remain !== null && last.remain !== undefined
        ? '（百度当日剩余配额：' + last.remain + ' 条，URL 多于配额时超出部分会推送失败）'
        : ''
      if (!confirm('将把「首页 + 全部分类列表页 + 全部上架商品详情页」一次性提交给百度' + tip + '，确定继续吗？')) return
      this.doBaiduPush({ type: 'all' })
    },
    // 推送自定义 URL
    pushCustomUrls () {
      const urls = this.seoCustomUrls.trim()
      if (!urls) {
        alert('请先填写要推送的 URL，例如：prodetail/12')
        return
      }
      this.doBaiduPush({ type: 'custom', urls })
    },
    // 统一调用推送接口，成功后刷新状态与日志
    doBaiduPush (payload) {
      this.seoPushing = true
      baiduPushApi(payload)
        .then((res) => {
          this.seoResult = res.data || null
          this.loadSeoStatus()
          this.loadSeoLog()
        })
        .catch((e) => {
          console.error('百度推送失败', e)
        })
        .finally(() => {
          this.seoPushing = false
        })
    },
    // 预览 sitemap 中的 URL 数量与示例
    loadSitemapUrls () {
      this.sitemapLoading = true
      getSitemapUrlsApi()
        .then((res) => {
          const data = res.data || {}
          this.sitemapTotal = data.total || 0
          this.sitemapSamples = (data.list || []).slice(0, 20)
        })
        .catch((e) => {
          console.error('sitemap 预览失败', e)
        })
        .finally(() => {
          this.sitemapLoading = false
        })
    },
    // 新窗口打开 sitemap.xml（未配置服务器重写时可访问 /server/sitemap.php）
    openSitemap () {
      window.open('/sitemap.xml', '_blank')
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品新增 / 编辑（独立页面） ----------
    // 打开独立的商品编辑页面：/admin/goods-form（带 id 为编辑）
    goGoodsForm (goodsId) {
      const id = Number(goodsId) || 0
      if (id > 0) {
        this.$router.push({ path: '/admin/goods-form', query: { id: String(id) } })
      } else {
        this.$router.push({ path: '/admin/goods-form' })
      }
    },
    // ---------- 商品 ----------
    async loadGoods (page) {
      try {
        // 超级管理员可按商家筛选（从「商家管理」点击“商品”进入）；普通商家后端自动限定本店铺
        const { data } = await getAdminGoodsListApi(page, this.goodsKeyword.trim(), this.goodsMerchantId)
        const list = (data && data.list) || []
        this.goodsList = list.map(item => ({
          ...item,
          show_on_home: item.show_on_home !== undefined ? item.show_on_home : 0,
          switchLoading: false
        }))
        this.goodsPage = data.page || 1
        this.goodsTotalPage = data.totalPage || 1
      } catch (e) {
        alert('商品列表加载失败')
        console.error(e)
      }
    },
    onGoodsPageChange (page) {
      this.loadGoods(page)
    },
    async toggleStatus (item) {
      try {
        const { message } = await toggleGoodsStatusApi(item.goods_id)
        alert(message || '操作成功')
        this.loadGoods(1)
      } catch (e) {
        alert('操作失败')
        console.error(e)
      }
    },
    async toggleHome (item) {
      if (item.switchLoading) return
      item.switchLoading = true

      const newValue = item.show_on_home === 1 ? 0 : 1
      const oldValue = item.show_on_home
      item.show_on_home = newValue

      try {
        await toggleHomeGoodsApi(item.goods_id)
        alert(newValue === 1 ? '✅ 已设为首页展示' : '❌ 已取消首页展示')
      } catch (e) {
        item.show_on_home = oldValue
        alert('操作失败，请重试')
        console.error(e)
      } finally {
        item.switchLoading = false
      }
    },
    deleteGoods (item) {
      if (!confirm(`确定删除「${item.goods_name}」吗？删除后不可恢复！`)) return
      
      deleteGoodsApi(item.goods_id)
        .then(() => {
          alert('删除成功')
          this.loadGoods(1)
        })
        .catch((e) => {
          alert('删除失败')
          console.error(e)
        })
    },

    // ---------- 订单 ----------
    switchOrderTab (key) {
      // 后台已按状态从服务端分页加载，切换时清空并重新拉取当前类型
      if (this.orderType === key && this.orderList.length > 0) return
      this.orderType = key
      this.orderPage = 1
      this.orderList = []
      this.loadOrders(1)
    },
    async loadOrders (page) {
      try {
        // 订单类型即后端 dataType（all / payment / groupwaiting / delivery / received / comment / refund / cancel）
        const dataType = this.orderType === 'all' ? 'all' : this.orderType
        const { data } = await getAdminOrderListApi(dataType, page, this.orderKeyword.trim())
        const list = (data && data.list) || []
        this.orderList = page === 1 ? list : this.orderList.concat(list)
        this.orderPage = data.page || 1
        this.orderTotalPage = data.totalPage || 1
        // 同步顶部统计卡（后端全量统计）
        this.loadOrderStats()
      } catch (e) {
        alert('订单列表加载失败')
        console.error(e)
      }
    },
    // 加载订单各状态统计（顶部统计卡使用，避免本地分页截断导致不准）
    async loadOrderStats () {
      try {
        const { data } = await getAdminOrderStatsApi()
        this.orderStatsData = data || null
      } catch (e) {
        // 统计加载失败时使用本地已加载数据兜底
        console.error('订单统计加载失败', e)
      }
    },
    // 按关键词搜索订单（订单号 / 手机号 / 昵称，全局搜索自动切到「全部」）
    searchOrders () {
      if (this.orderType !== 'all') {
        this.orderType = 'all'
        this.orderList = []
      }
      this.orderPage = 1
      this.loadOrders(1)
    },
    // 清空关键词并刷新
    resetOrderSearch () {
      this.orderKeyword = ''
      this.searchOrders()
    },

    // 订单发货（待发货 -> 待收货）
    shipOrder (order) {
      if (!confirm('确定发货吗？发货后订单状态将变为"待收货"。')) return
      
      shipOrderApi(order.order_id)
        .then(() => {
          alert('发货成功')
          this.loadOrders(1)
        })
        .catch((e) => {
          alert('发货失败')
          console.error(e)
        })
    },
    // 取消订单
    cancelOrder (order) {
      if (!confirm('确定取消该订单吗？')) return
      
      cancelOrderApi(order.order_id)
        .then(() => {
          alert('订单已取消')
          this.loadOrders(1)
        })
        .catch((e) => {
          alert('取消失败')
          console.error(e)
        })
    },
    // 批量发货
    batchShip () {
      const pending = this.pendingOrders
      if (pending.length === 0) {
        alert('没有待发货的订单')
        return
      }

      if (!confirm(`确定对 ${pending.length} 个待发货订单进行批量发货吗？`)) return

      const orderIds = pending.map(o => o.order_id)
      batchShipOrderApi(orderIds)
        .then((result) => {
          alert(`批量发货成功！共处理 ${result.data?.successCount || orderIds.length} 个订单`)
          this.loadOrders(1)
        })
        .catch((e) => {
          alert('批量发货失败，请重试')
          console.error(e)
        })
    },
    // 确认收货（待收货 -> 已完成）
    confirmReceive (order) {
      if (!confirm('确认已收到货吗？确认后订单将变为"已完成"。')) return
      
      // 调用确认收货接口
      confirmOrderApi(order.order_id)
        .then(() => {
          alert('确认收货成功')
          this.loadOrders(1)
        })
        .catch((e) => {
          alert('确认收货失败，请重试')
          console.error(e)
        })
    },
    // 同意退款
    approveRefund (order) {
      const refund = order.refund
      if (!refund) {
        alert('退款信息异常')
        return
      }
      const reply = prompt('请输入退款处理备注（可留空）：', '退款已受理')
      if (reply === null) return
      if (!confirm(`确定同意对订单「${order.order_no}」退款 ¥${order.total_price} 吗？`)) return
      handleRefundApi(refund.id, 'approve', reply.trim())
        .then(() => {
          alert('退款成功，款项已原路退回')
          this.loadOrders(1)
        })
        .catch((e) => {
          alert(typeof e === 'string' ? e : '处理失败，请重试')
          console.error(e)
        })
    },
    // 拒绝退款
    rejectRefund (order) {
      const refund = order.refund
      if (!refund) {
        alert('退款信息异常')
        return
      }
      const reply = prompt('请输入拒绝退款的原因（将展示给买家）：', '暂不符合退款条件')
      if (reply === null) return
      if (!confirm(`确定拒绝订单「${order.order_no}」的退款申请吗？`)) return
      handleRefundApi(refund.id, 'reject', reply.trim())
        .then(() => {
          alert('已拒绝退款，订单状态已恢复')
          this.loadOrders(1)
        })
        .catch((e) => {
          alert(typeof e === 'string' ? e : '处理失败，请重试')
          console.error(e)
        })
    },


    // ---------- 数据分析 ----------
    initStatsDate () {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      this.statsStartDate = `${y}-${m}-${d}`
      this.statsEndDate = `${y}-${m}-${d}`
    },
    resetStatsDate () {
      this.initStatsDate()
      this.loadStats()
    },
    loadStats () {
      if (!this.statsStartDate || !this.statsEndDate) {
        alert('请选择开始和结束日期')
        return
      }
      if (this.statsStartDate > this.statsEndDate) {
        alert('开始日期不能晚于结束日期')
        return
      }
      getStatsOverviewApi(this.statsStartDate, this.statsEndDate)
        .then((res) => {
          this.statsData = res.data || this.statsData
        })
        .catch((e) => {
          alert('统计数据加载失败')
          console.error(e)
        })
    },
    formatSales (val) {
      const num = Number(val) || 0
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
  }
}
</script>

<style lang="less" scoped>
// ============================================================
//  全局布局
// ============================================================
.admin-container {
  display: flex;
  min-height: 100vh;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

// ============================================================
//  侧边栏
// ============================================================
.admin-sidebar {
  width: 220px;
  min-height: 100vh;
  background: #1a1a2e;
  color: #a0a0b8;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100vh;
  overflow-y: auto;

  .sidebar-header {
    padding: 24px 20px 20px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
    .logo {
      display: flex;
      align-items: center;
      gap: 12px;
      .logo-icon { font-size: 28px; }
      .logo-text { font-size: 18px; font-weight: 700; color: #fff; letter-spacing: 1px; }
    }
  }

  .sidebar-nav {
    flex: 1;
    padding: 16px 12px;
    .nav-item {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      border-radius: 10px;
      cursor: pointer;
      transition: all 0.25s;
      margin-bottom: 4px;
      color: #a0a0b8;
      &:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }
      &.active {
        background: rgba(99, 102, 241, 0.25);
        color: #fff;
        box-shadow: 0 0 20px rgba(99, 102, 241, 0.15);
        .nav-icon { color: #818cf8; }
      }
      .nav-icon { font-size: 18px; margin-right: 12px; width: 24px; text-align: center; flex-shrink: 0; }
      .nav-label { flex: 1; font-size: 14px; font-weight: 500; }
    }
  }

  .sidebar-footer {
    padding: 16px 20px 24px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    .back-btn {
      width: 100%;
      padding: 10px;
      background: transparent;
      border: 1px solid rgba(255, 255, 255, 0.12);
      border-radius: 8px;
      color: #a0a0b8;
      cursor: pointer;
      transition: all 0.25s;
      font-size: 14px;
      &:hover { color: #fff; border-color: rgba(255, 255, 255, 0.3); }
    }
  }
}

// ============================================================
//  主内容区
// ============================================================
.admin-main {
  flex: 1;
  padding: 0 24px 24px;
  max-width: calc(100vw - 220px);
  overflow-x: hidden;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0 16px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 24px;
  .page-title { font-size: 22px; font-weight: 700; color: #1a1a2e; margin: 0; }
  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    .admin-avatar {
      padding: 6px 14px;
      border-radius: 18px;
      background: linear-gradient(135deg, #818cf8, #6366f1);
      color: #fff;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 600;
      white-space: nowrap;
    }
    .header-btn {
      padding: 6px 14px;
      border: 1px solid #d1d5db;
      border-radius: 18px;
      background: #fff;
      font-size: 13px;
      color: #4b5563;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
      &:hover { border-color: #6366f1; color: #6366f1; background: #eef2ff; }
      &:active { transform: scale(0.96); }
      &.logout:hover { border-color: #f43f5e; color: #f43f5e; background: #fff1f2; }
      &.service:hover { border-color: #ff6b35; color: #ff6b35; background: #fff7f0; }
    }

    // 角色标识（超级管理员 / 普通商家）
    .role-tag {
      padding: 4px 12px;
      border-radius: 14px;
      font-size: 12px;
      white-space: nowrap;

      &.super { background: #fff7e6; color: #d48806; border: 1px solid #ffe0a3; }
      &.merchant { background: #eef7ff; color: #1677ff; border: 1px solid #cfe6ff; }
    }
  }
}

// ============================================================
//  商品管理：商家筛选提示 与 商品数据分析（需求 5）
// ============================================================
.merchant-filter-tip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-right: 10px;
  padding: 6px 12px;
  border-radius: 16px;
  background: #fff7f0;
  border: 1px dashed #ffd2b0;
  color: #b06a00;
  font-size: 12px;

  .clear-btn {
    border: none;
    background: #ff6b35;
    color: #fff;
    border-radius: 10px;
    padding: 2px 8px;
    font-size: 12px;
    cursor: pointer;
  }
}

.shop-name-text { color: #6366f1; font-size: 12px; }

.goods-stats-block {
  margin-top: 22px;

  .block-title {
    font-size: 15px;
    font-weight: 700;
    color: #1a1a2e;
    margin-bottom: 12px;

    .block-sub {
      margin-left: 10px;
      font-size: 12px;
      font-weight: 400;
      color: #9ca3af;
    }
  }

  .gs-img {
    width: 42px;
    height: 42px;
    object-fit: cover;
    border-radius: 6px;
  }

  .gs-name { font-size: 13px; color: #1f2937; }
  .gs-sub { font-size: 12px; color: #9ca3af; margin-top: 2px; }
  .gs-money { color: #ff6b35; font-weight: 700; }
}

// ============================================================
//  统计卡片
// ============================================================
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
  .stat-card {
    background: #fff;
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    align-items: center;
    gap: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
    border: 1px solid rgba(0, 0, 0, 0.04);
    &:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08); }
    .stat-icon {
      font-size: 28px; width: 48px; height: 48px; border-radius: 12px;
      display: flex; align-items: center; justify-content: center; flex-shrink: 0;
      &.goods-icon { background: #eef2ff; }
      &.on-sale-icon { background: #ecfdf5; }
      &.off-sale-icon { background: #fef2f2; }
      &.stat-all { background: #eff6ff; }
      &.stat-pending { background: #fffbeb; }
      &.stat-done { background: #ecfdf5; }
      &.stat-cancel { background: #fef2f2; }
      &.stat-received { background: #ede9fe; }
      &.sales-icon { background: #ecfdf5; }
      &.visitor-icon { background: #eff6ff; }
      &.view-icon { background: #fffbeb; }
      &.comment-icon { background: #fdf2f8; }
    }
    .stat-info {
      .stat-value { font-size: 24px; font-weight: 700; color: #1a1a2e; line-height: 1.2; }
      .stat-value.stat-money { color: #059669; font-size: 22px; }
      .stat-label { font-size: 13px; color: #6b7280; margin-top: 2px; }
    }
  }
}

// ============================================================
//  工具栏
// ============================================================
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff;
  padding: 16px 20px;
  border-radius: 12px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  flex-wrap: wrap;
  gap: 12px;
  .toolbar-left { flex: 1; min-width: 200px; max-width: 500px; }
  .search-wrapper {
    display: flex;
    align-items: center;
    background: #f1f3f5;
    border-radius: 10px;
    padding: 0 4px 0 14px;
    transition: all 0.3s ease;
    border: 2px solid transparent;
    &:focus-within {
      background: #fff;
      border-color: #6366f1;
      box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
    }
    .search-icon { color: #9ca3af; font-size: 18px; flex-shrink: 0; }
    .search-input-modern {
      flex: 1; border: none; outline: none; background: transparent;
      padding: 10px 12px; font-size: 14px; color: #1a1a2e; min-width: 120px;
      &::placeholder { color: #9ca3af; }
    }
    .search-btn {
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      border: none; color: #fff; padding: 8px 18px; border-radius: 8px;
      font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; white-space: nowrap;
      &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35); }
      &:active { transform: scale(0.96); }
    }
  }
  .add-btn {
    background: linear-gradient(135deg, #10b981, #059669);
    border: none; color: #fff; padding: 10px 20px; border-radius: 8px;
    font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.3s ease;
    &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(16, 185, 129, 0.35); }
    &:active { transform: scale(0.96); }
  }
}

// ============================================================
//  数据分析 - 日期筛选
// ============================================================
.stats-toolbar {
  .stats-filter {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    label { font-size: 13px; color: #6b7280; font-weight: 500; }
    .date-sep { color: #9ca3af; font-size: 14px; }

    input[type='date'] {
      padding: 8px 12px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      font-size: 14px;
      color: #1a1a2e;
      background: #f9fafb;
      transition: all 0.2s;
      &:focus {
        outline: none;
        border-color: #6366f1;
        background: #fff;
        box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1);
      }
    }

    .search-btn {
      background: linear-gradient(135deg, #6366f1, #8b5cf6);
      border: none; color: #fff; padding: 9px 20px; border-radius: 8px;
      font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; white-space: nowrap;
      &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35); }
      &:active { transform: scale(0.96); }
    }

    .reset-btn {
      padding: 9px 16px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      background: #fff;
      font-size: 14px;
      color: #6b7280;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
      &:hover { border-color: #6366f1; color: #6366f1; background: #eef2ff; }
      &:active { transform: scale(0.96); }
    }
  }
}

.stats-date-tip {
  margin-top: 4px;
  text-align: right;
  font-size: 13px;
  color: #9ca3af;
}

// ============================================================
//  表格
// ============================================================
.table-wrapper {
  background: #fff;
  border-radius: 12px;
  padding: 0 4px 4px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  .admin-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 14px;
    thead {
      background: #f8fafc;
      th { font-weight: 600; color: #475569; font-size: 13px; padding: 12px; text-align: left; border-bottom: 2px solid #e5e7eb; white-space: nowrap; }
    }
    tbody {
      td { padding: 12px; vertical-align: middle; border-bottom: 1px solid #f1f5f9; }
      tr:hover { background: #f8fafc; }
    }
  }
  .table-img { width: 56px; height: 56px; border-radius: 8px; object-fit: cover; background: #f1f5f9; }
  .goods-name-cell {
    .name-main { font-weight: 500; color: #1a1a2e; }
    .name-sub { font-size: 12px; color: #94a3b8; margin-top: 2px; }
    .sku-badge {
      display: inline-block;
      margin-top: 4px;
      padding: 1px 8px;
      border-radius: 10px;
      background: #eef2ff;
      color: #6366f1;
      font-size: 11px;
    }
    .sku-badge.group-badge {
      background: #fff1e6;
      color: #ff6b35;
      margin-left: 4px;
    }
  }
  .price-text { color: #f59e0b; font-weight: 600; }

  .home-switch-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    .switch-label { font-size: 12px; font-weight: 500; min-width: 32px; &.show { color: #07c160; } &.hide { color: #999; } }
  }

  .action-btns {
    display: flex; gap: 6px; flex-wrap: wrap;
    .action-btn {
      padding: 4px 12px; border: 1px solid #ddd; border-radius: 6px; font-size: 12px; cursor: pointer; background: #fff; transition: all 0.2s;
      &.edit { border-color: #6366f1; color: #6366f1; &:hover { background: #eef2ff; } }
      &.off { border-color: #f59e0b; color: #f59e0b; &:hover { background: #fffbeb; } }
      &.on { border-color: #10b981; color: #10b981; &:hover { background: #ecfdf5; } }
      &.delete { border-color: #ef4444; color: #ef4444; &:hover { background: #fef2f2; } }
    }
  }

  .table-footer {
    display: flex; justify-content: space-between; align-items: center; padding: 12px 16px;
    border-top: 1px solid #f1f5f9; background: #fafbfc;
    .total-info { font-size: 13px; color: #6b7280; }
    .pagination {
      display: flex; align-items: center; gap: 8px;
      .page-btn {
        padding: 4px 14px; border: 1px solid #ddd; border-radius: 6px; background: #fff; cursor: pointer; font-size: 13px; transition: all 0.2s;
        &:hover:not(:disabled) { background: #f3f4f6; }
        &:disabled { opacity: 0.5; cursor: not-allowed; }
      }
      .page-num { font-size: 13px; color: #6b7280; min-width: 56px; text-align: center; }
    }
  }
}

// ============================================================
//  自定义开关
// ============================================================
.custom-switch {
  cursor: pointer;
  user-select: none;
  .switch-track {
    width: 40px; height: 22px; background: #d1d5db; border-radius: 11px;
    position: relative; transition: background 0.3s; display: flex; align-items: center; padding: 2px;
  }
  .switch-thumb {
    width: 18px; height: 18px; background: #fff; border-radius: 50%;
    transition: transform 0.3s; box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  }
  &.active .switch-track { background: #07c160; }
  &.active .switch-thumb { transform: translateX(18px); }
  &.small .switch-track { width: 36px; height: 20px; }
  &.small .switch-thumb { width: 16px; height: 16px; }
  &.small.active .switch-thumb { transform: translateX(16px); }
}

// ============================================================
//  状态标签
// ============================================================
.status-tag {
  display: inline-block; padding: 2px 10px; border-radius: 4px; font-size: 12px; font-weight: 500;
  &.on-sale { background: #e8f5e9; color: #4caf50; }
  &.off-sale { background: #fce4ec; color: #f44336; }
  &.status-pending { background: #fff3e0; color: #f57c00; }
  &.status-shipped { background: #e3f2fd; color: #1976d2; }
  &.status-received { background: #ede9fe; color: #7c3aed; }
  &.status-refund { background: #fff7ed; color: #ea580c; }
  &.status-done { background: #e8f5e9; color: #4caf50; }
  &.status-cancel { background: #fce4ec; color: #f44336; }
  &.status-default { background: #f5f5f5; color: #999; }
}

// ============================================================
//  空状态
// ============================================================
.empty-state {
  text-align: center; padding: 80px 0; background: #fff; border-radius: 12px;
  p { color: #999; margin-top: 12px; font-size: 14px; }
}

// ============================================================
//  订单筛选
// ============================================================
.order-filter-bar {
  background: #fff; border-radius: 12px; padding: 8px 16px; margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06); border: 1px solid rgba(0, 0, 0, 0.04);
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 12px;
  .filter-tabs-modern {
    display: flex; gap: 4px; flex-wrap: wrap;
    .filter-tab {
      padding: 8px 16px; border: none; background: transparent; border-radius: 8px;
      font-size: 14px; color: #6b7280; cursor: pointer; transition: all 0.25s ease; font-weight: 500;
      &:hover { background: #f3f4f6; color: #1a1a2e; }
      &.active { background: #eef2ff; color: #6366f1; font-weight: 600; }
      .tab-count {
        display: inline-block; background: #f3f4f6; color: #6b7280; font-size: 11px;
        padding: 0 6px; border-radius: 10px; margin-left: 4px; min-width: 18px; text-align: center;
      }
    }
  }
  // 搜索框 + 批量发货按钮：同一行显示（flex 排列，空间不足时自动换行）
  .filter-actions {
    display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-left: auto;
  }

  .order-search-wrapper {
    display: flex; align-items: center; background: #f1f3f5; border-radius: 10px;
    padding: 0 4px 0 12px; border: 2px solid transparent; transition: all 0.3s ease;
    &:focus-within { background: #fff; border-color: #6366f1; box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.1); }
    .search-icon { color: #9ca3af; font-size: 16px; flex-shrink: 0; }
    .order-search-input {
      flex: 1; border: none; outline: none; background: transparent; padding: 8px 10px;
      font-size: 13px; color: #1a1a2e; min-width: 130px;
      &::placeholder { color: #9ca3af; }
    }
    .search-btn {
      background: linear-gradient(135deg, #6366f1, #8b5cf6); border: none; color: #fff;
      padding: 7px 14px; border-radius: 8px; font-size: 13px; cursor: pointer; white-space: nowrap;
      transition: all 0.3s ease;
      &:hover { transform: translateY(-1px); box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35); }
    }
    .reset-btn {
      background: transparent; border: none; color: #9ca3af; font-size: 13px;
      padding: 7px 8px; cursor: pointer; white-space: nowrap;
      &:hover { color: #ef4444; }
    }
  }

  .batch-btn {
    padding: 6px 16px; background: #10b981; border: none; border-radius: 8px;
    color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.3s;
    white-space: nowrap; flex-shrink: 0;
    &:hover { background: #059669; transform: translateY(-1px); }

  }
}

// ============================================================
//  订单卡片
// ============================================================
.order-list {
  display: flex; flex-direction: column; gap: 16px;
  .order-card {
    background: #fff; border-radius: 12px; padding: 20px 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06); border: 1px solid rgba(0, 0, 0, 0.04);
    &:hover { box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08); }
    .order-header {
      display: flex; justify-content: space-between; align-items: center; padding-bottom: 14px; border-bottom: 1px solid #f1f5f9;
      .order-info { display: flex; align-items: center; gap: 20px; .order-no { font-weight: 600; color: #1a1a2e; font-size: 14px; } .order-time { font-size: 13px; color: #94a3b8; } }
      .order-buyer { display: flex; align-items: center; gap: 8px; font-size: 13px; color: #475569; flex-shrink: 0;
        .buyer-item { display: inline-flex; align-items: center; }
        .buyer-mobile { font-weight: 600; color: #1a1a2e; }
        .buyer-nick { color: #94a3b8; }
      }

    }
    .order-body {
      display: flex; justify-content: space-between; align-items: center; padding: 14px 0; gap: 20px;
      .order-goods-list {
        display: flex; flex-wrap: wrap; gap: 12px; flex: 1;
        .order-goods-item {
          display: flex; align-items: center; gap: 8px; background: #f8fafc; padding: 4px 12px 4px 4px; border-radius: 8px;
          img { width: 36px; height: 36px; border-radius: 6px; object-fit: cover; }
          .goods-name { font-size: 13px; color: #1a1a2e; max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            .goods-spec-text { display: block; font-size: 11px; color: #94a3b8; }
          }
          .goods-qty { font-size: 12px; color: #94a3b8; margin-left: 4px; }
        }
      }
      .order-summary {
        text-align: right; flex-shrink: 0;
        .total-qty { font-size: 13px; color: #6b7280; display: block; }
        .total-price { font-size: 18px; font-weight: 700; color: #f59e0b; }
      }
    }
    .order-footer {
      padding-top: 14px; border-top: 1px solid #f1f5f9; display: flex; gap: 10px; justify-content: flex-end;
      .ship-btn { 
        padding: 6px 16px; background: #10b981; border: none; border-radius: 6px; 
        color: #fff; font-size: 13px; cursor: pointer; transition: all 0.2s; 
        &:hover { background: #059669; } 
      }
      .cancel-btn { 
        padding: 6px 16px; background: transparent; border: 1px solid #ef4444; 
        border-radius: 6px; color: #ef4444; font-size: 13px; cursor: pointer; transition: all 0.2s; 
        &:hover { background: #fef2f2; } 
      }
      .confirm-receive-btn {
        padding: 6px 16px; background: #6366f1; border: none; border-radius: 6px;
        color: #fff; font-size: 13px; cursor: pointer; transition: all 0.2s;
        &:hover { background: #4f46e5; }
      }
      .refund-approve-btn {
        padding: 6px 16px; background: #ea580c; border: none; border-radius: 6px;
        color: #fff; font-size: 13px; cursor: pointer; transition: all 0.2s;
        &:hover { background: #c2410c; }
      }
      .refund-reject-btn {
        padding: 6px 16px; background: transparent; border: 1px solid #6b7280;
        border-radius: 6px; color: #6b7280; font-size: 13px; cursor: pointer; transition: all 0.2s;
        &:hover { background: #f3f4f6; }
      }
      .refund-reason {
        flex: 1; text-align: left; font-size: 13px; color: #ea580c;
        word-break: break-all;
      }

    }
  }
}
.load-more { text-align: center; padding: 16px 0; .load-more-btn { padding: 8px 30px; background: #e5e7eb; border: none; border-radius: 8px; font-size: 14px; cursor: pointer; transition: all 0.2s; &:hover { background: #d1d5db; } } }

// ============================================================
//  弹窗
// ============================================================
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;

  .modal-content {
    background: #fff;
    border-radius: 16px;
    width: 520px;
    max-height: 90vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-bottom: 1px solid #f1f5f9;
      font-size: 18px;
      font-weight: 600;

      .modal-close {
        background: none;
        border: none;
        font-size: 22px;
        cursor: pointer;
        color: #999;
        transition: color 0.2s;
        &:hover { color: #333; }
      }
    }

    .modal-body {
      padding: 20px 24px;
      overflow-y: auto;
      flex: 1;

      .form-group {
        margin-bottom: 16px;

        label {
          display: block;
          font-size: 13px;
          font-weight: 500;
          color: #333;
          margin-bottom: 4px;
          .required { color: #ef4444; }
        }

        input, textarea {
          width: 100%;
          padding: 8px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          transition: border 0.2s;
          box-sizing: border-box;
          &:focus {
            outline: none;
            border-color: #6366f1;
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }
        }

        textarea {
          resize: vertical;
          min-height: 80px;
          font-family: inherit;
        }

        &.switch-group {
          display: flex;
          align-items: center;
          gap: 12px;
          label { margin-bottom: 0; min-width: 70px; }
        }
      }

      .form-tip {
        margin-top: 4px;
        font-size: 12px;
        color: #9ca3af;
        background: #f8fafc;
        border-radius: 8px;
        padding: 10px 12px;
        line-height: 1.6;
      }

      .form-row {
        display: flex;
        gap: 12px;
        .half { flex: 1; }
      }
    }

    .modal-footer {
      display: flex;
      gap: 12px;
      padding: 16px 24px;
      border-top: 1px solid #f1f5f9;
      justify-content: flex-end;

      .modal-btn {
        padding: 8px 24px;
        border: none;
        border-radius: 8px;
        font-size: 14px;
        cursor: pointer;
        transition: all 0.2s;

        &.cancel {
          background: #f3f4f6;
          color: #6b7280;
          &:hover { background: #e5e7eb; }
        }

        &.confirm {
          background: linear-gradient(135deg, #6366f1, #8b5cf6);
          color: #fff;
          &:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.35);
          }
        }
      }
    }
  }
}

// ============================================================
//  响应式
// ============================================================
@media (max-width: 1024px) {
  .admin-sidebar { width: 72px; .logo-text, .nav-label, .sidebar-footer .back-btn span { display: none; } .sidebar-header { padding: 16px 0; text-align: center; .logo-icon { font-size: 30px; } } .sidebar-nav .nav-item { justify-content: center; padding: 12px 0; .nav-icon { margin-right: 0; font-size: 22px; } } }
  .admin-main { max-width: calc(100vw - 72px); padding: 0 16px 16px; }
}
@media (max-width: 768px) {
  .admin-sidebar { display: none; }
  .admin-main { max-width: 100vw; padding: 0 12px 16px; }
  .admin-header .page-title { font-size: 18px; }
  .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; .stat-card { padding: 14px 16px; } }
  .toolbar { flex-direction: column; align-items: stretch; .toolbar-left { max-width: 100%; } }
  .stats-toolbar .stats-filter { flex-direction: column; align-items: stretch; input[type='date'] { width: 100%; } }
  .table-wrapper { overflow-x: auto; }
  .order-filter-bar { flex-direction: column; align-items: stretch; }
  .order-card { padding: 16px !important; .order-body { flex-direction: column; align-items: stretch !important; .order-summary { text-align: left !important; display: flex; justify-content: space-between; } } .order-footer { flex-direction: column; .ship-btn, .cancel-btn, .confirm-receive-btn { width: 100%; } } }
  .modal-content { width: 92% !important; }
}

// ============================================================
//  图片空间
// ============================================================
.toolbar-title { font-size: 14px; color: #6b7280; font-weight: 500; }
.toolbar-tip { font-size: 12px; color: #9ca3af; margin-left: 6px; }
.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}
.image-card {
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #f0f0f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1); }
  img { width: 100%; height: 150px; object-fit: cover; display: block; background: #f7f8fa; }
  .image-info {
    padding: 8px 10px;
    .image-name { display: block; font-size: 12px; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .image-time { display: block; font-size: 11px; color: #9ca3af; margin-top: 2px; }
    .image-uploader { display: block; font-size: 11px; color: #f59e0b; margin-top: 2px; }
  }
  .image-actions {
    display: flex;
    border-top: 1px solid #f3f4f6;
    button {
      flex: 1;
      padding: 7px 0;
      border: none;
      background: #fff;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;
    }
    .image-use-btn { color: #6366f1; &:hover { background: #eef2ff; } }
    .image-del-btn { color: #f43f5e; border-left: 1px solid #f3f4f6; &:hover { background: #fff1f2; } }
  }
}
.empty-state {
  text-align: center;
  padding: 60px 0;
  color: #9ca3af;
  p { margin-top: 12px; font-size: 14px; }
}

// ============================================================
//  系统设置
// ============================================================
.settings-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.04);
  .settings-card-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin-bottom: 6px; }
  .settings-card-desc { font-size: 13px; color: #9ca3af; margin-bottom: 16px; }
}
.settings-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
  &:last-of-type { border-bottom: none; }
  .settings-label { display: flex; align-items: center; gap: 12px; }
  .method-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 34px; height: 34px; border-radius: 8px; color: #fff; font-size: 16px; font-weight: 700;
    &.wechat { background: #07c160; }
    &.alipay { background: #1677ff; }
    &.balance { background: #ffb400; }
  }
  .settings-text {
    .settings-name { font-size: 14px; font-weight: 600; color: #1f2937; }
    .settings-desc { font-size: 12px; color: #9ca3af; margin-top: 2px; }
  }
}
.settings-actions {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.form-tip { font-size: 12px; color: #9ca3af; margin: 6px 0 10px; }

// 自定义开关
.custom-switch {
  display: inline-block;
  cursor: pointer;
  .switch-track {
    width: 44px; height: 24px;
    border-radius: 12px;
    background: #d1d5db;
    position: relative;
    transition: background 0.2s;
    .switch-thumb {
      position: absolute;
      top: 2px; left: 2px;
      width: 20px; height: 20px;
      border-radius: 50%;
      background: #fff;
      transition: transform 0.2s;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
  }
  &.active .switch-track { background: #6366f1; .switch-thumb { transform: translateX(20px); } }
  &.small .switch-track { width: 36px; height: 20px; .switch-thumb { width: 16px; height: 16px; top: 2px; left: 2px; } &.active .switch-thumb { transform: translateX(16px); } }
}

// ============================================================
//  商品表单 - 图片选择 & 规格管理
// ============================================================
.image-url-row {
  display: flex;
  gap: 8px;
  input { flex: 1; }
}
.picker-btn {
  flex-shrink: 0;
  padding: 0 14px;
  border: 1px solid #6366f1;
  border-radius: 8px;
  background: #eef2ff;
  color: #6366f1;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  &:hover { background: #6366f1; color: #fff; }
  &.small { padding: 0 10px; font-size: 12px; }
}
.sku-section {
  .sku-row {
    background: #f9fafb;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    .sku-fields {
      display: flex;
      gap: 8px;
      margin-bottom: 8px;
      .sku-spec-input { flex: 2; }
      .sku-price-input, .sku-stock-input { flex: 1; }
    }
    .sku-image-row { display: flex; gap: 8px; input { flex: 1; } }
    position: relative;
    .sku-del-btn {
      position: absolute;
      top: -8px; right: -8px;
      width: 22px; height: 22px;
      border-radius: 50%;
      border: none;
      background: #f43f5e;
      color: #fff;
      font-size: 12px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      &:hover { background: #e11d48; }
    }
  }
  .add-sku-btn {
    width: 100%;
    padding: 8px 0;
    border: 1px dashed #c7c9d4;
    border-radius: 8px;
    background: #fff;
    color: #6366f1;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { border-color: #6366f1; background: #eef2ff; }
  }
}

// 自动发货（虚拟卡密）设置区
.auto-delivery-section {
  .auto-row {
    background: #f9fafb;
    border: 1px solid #f0f0f0;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;

    .auto-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 8px;

      .auto-sku {
        font-size: 13px;
        font-weight: 600;
        color: #374151;
      }

      .auto-toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;
        color: #4b5563;
        cursor: pointer;
      }
    }

    .auto-opt {
      display: block;
      font-size: 12px;
      color: #4b5563;
      margin-bottom: 6px;
      cursor: pointer;

      input {
        margin-right: 6px;
      }
    }

    textarea {
      width: 100%;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 8px 10px;
      font-size: 12px;
      font-family: inherit;
      resize: vertical;
      outline: none;
      box-sizing: border-box;
    }

    .card-manage {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-top: 8px;

      .card-stat {
        font-size: 12px;
        color: #6b7280;

        b {
          color: #f43f5e;
        }
      }
    }
  }
}

// 图片空间选择弹窗
.image-picker-modal {
  max-width: 720px;
  width: 720px;
  .picker-toolbar {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 14px;
  }
  .picker-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    max-height: 380px;
    overflow-y: auto;
    padding: 4px;
    .image-card img { height: 110px; }
  }
}

// ============================================================
//  百度收录（主动推送）
// ============================================================
.seo-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 10px 24px;
  margin-bottom: 12px;
  .seo-info-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: #f8fafc;
    border-radius: 8px;
    .seo-info-label { font-size: 13px; color: #9ca3af; flex-shrink: 0; }
    .seo-info-value {
      font-size: 13px; font-weight: 600; color: #1f2937;
      word-break: break-all;
      &.off { color: #ef4444; }
    }
  }
}
.seo-url-tip { font-size: 12px; color: #6366f1; margin-bottom: 6px; word-break: break-all; }
.seo-textarea {
  width: 100%;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.7;
  font-family: ui-monospace, Menlo, Consolas, monospace;
  color: #1f2937;
  resize: vertical;
  box-sizing: border-box;
  &:focus { outline: none; border-color: #6366f1; }
}
.seo-result {
  font-size: 14px;
  font-weight: 600;
  color: #07c160;
  margin-bottom: 6px;
  &.fail { color: #ef4444; }
}
.seo-result-detail { font-size: 12px; color: #9ca3af; }
.seo-log-file { font-size: 12px; font-weight: 400; color: #9ca3af; margin-left: 8px; }
.seo-log-list {
  max-height: 320px;
  overflow-y: auto;
  background: #f8fafc;
  border-radius: 8px;
  padding: 10px 12px;
  .seo-log-item {
    font-size: 12px;
    line-height: 1.9;
    color: #4b5563;
    font-family: ui-monospace, Menlo, Consolas, monospace;
    word-break: break-all;
    border-bottom: 1px dashed #e5e7eb;
    &:last-child { border-bottom: none; }
  }
}
</style>
